import type { ComponentChildren } from 'preact';

/**
 * Two-tier button system for the admin surface.
 *
 *   • PrimaryActionButton - filled accent pill, sans-serif, page-level
 *     (Save, Import, Run all). One per surface section, usually.
 *   • ActionButton - outlined mono uppercase pill, in-row (Run, View,
 *     Cancel, Suppress). Multiple per row is fine.
 *
 * Both expose `busy`/`disabled`. `busy` is a soft-disable that also
 * renders a spinner glyph so the user sees the action in flight.
 */
interface SharedProps {
    children: ComponentChildren;
    onClick?: (e: MouseEvent) => void;
    variant?: 'default' | 'danger';
    busy?: boolean;
    disabled?: boolean;
    type?: 'button' | 'submit' | 'reset';
    title?: string;
    class?: string;
}

const PRIMARY_VARIANT: Record<NonNullable<SharedProps['variant']>, string> = {
    default:
        'bg-accent text-surface-base hover:bg-accent-soft hover:text-accent-ink focus-visible:ring-accent/50',
    danger:
        'bg-state-failed text-surface-base hover:bg-state-failed/85 focus-visible:ring-state-failed/50',
};

const ACTION_VARIANT: Record<NonNullable<SharedProps['variant']>, string> = {
    default:
        'border-rule-strong text-fg-primary hover:border-accent hover:text-accent focus-visible:ring-accent/40',
    danger:
        'border-state-failed/40 text-state-failed hover:border-state-failed hover:bg-state-failed/10 focus-visible:ring-state-failed/40',
};

export function PrimaryActionButton({
    children,
    onClick,
    variant = 'default',
    busy = false,
    disabled = false,
    type = 'button',
    title,
    class: extraClass,
}: SharedProps) {
    const isDisabled = busy || disabled;
    return (
        <button
            type={type}
            title={title}
            onClick={onClick}
            disabled={isDisabled}
            class={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50 ${PRIMARY_VARIANT[variant]} ${extraClass ?? ''}`}
        >
            {busy && <Spinner />}
            {children}
        </button>
    );
}

export function ActionButton({
    children,
    onClick,
    variant = 'default',
    busy = false,
    disabled = false,
    type = 'button',
    title,
    class: extraClass,
}: SharedProps) {
    const isDisabled = busy || disabled;
    return (
        <button
            type={type}
            title={title}
            onClick={onClick}
            disabled={isDisabled}
            class={`inline-flex items-center gap-1.5 rounded-full border bg-transparent px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] transition-colors focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50 ${ACTION_VARIANT[variant]} ${extraClass ?? ''}`}
        >
            {busy && <Spinner />}
            {children}
        </button>
    );
}

function Spinner() {
    return (
        <span
            aria-hidden="true"
            class="inline-block h-2.5 w-2.5 animate-spin rounded-full border border-current border-r-transparent"
        />
    );
}
